/* eslint-disable react/prop-types */
/**
 * MedicationReferralDetailsDialog — the "Medication Referral Details"
 * (view/eyeball) dialog, extracted VERBATIM from App.js (Jun 2026, same
 * strangler pattern as PrepareClaimDialog).  State + handlers arrive via
 * `ctx`; hosts: legacy App.js AND V2 MedicationManagementRoute.
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DebouncedTextarea, DebouncedInput } from '../ui/debounced-textarea';
import { Check, MessageSquare } from 'lucide-react';
import { formatMrn } from '../../utils/formatMrn';
import { getOptionLabel } from '../../utils/helpers';

export const MedicationReferralDetailsDialog = ({ ctx }) => {
  const {
    API,
    allClientsForLookup,
    editingReferralFormData,
    fetchMedicationReferralRequests,
    formTemplates,
    formatClientName,
    medicationReferralTemplate,
    selectedReferralForDetails,
    serviceRequestTemplates,
    setEditingReferralFormData,
    setSelectedReferralForDetails,
    user,
  } = ctx;

  return (
      <Dialog open={selectedReferralForDetails !== null} onOpenChange={() => {
        setSelectedReferralForDetails(null);
        setEditingReferralFormData(null);
      }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Medication Referral Details</DialogTitle>
          </DialogHeader>
          {selectedReferralForDetails && (() => {
            const request = selectedReferralForDetails;
            const requestClient = allClientsForLookup.find(c => c.id === request.client_id);
            const template = formTemplates.find(t => t.id === request.template_id) || 
                           serviceRequestTemplates.find(t => t.id === request.template_id) ||
                           (medicationReferralTemplate?.id === request.template_id ? medicationReferralTemplate : null);
            
            // Check if user can edit (is requester and status is needs_more_info)
            const canEdit = request.status === 'needs_more_info' && request.requested_by_id === user?.id;
            
            // Initialize editing form data if not set and user can edit
            if (canEdit && !editingReferralFormData) {
              // Use setTimeout to avoid setState during render
              setTimeout(() => setEditingReferralFormData({...request.form_data}), 0);
            }
            
            const currentFormData = canEdit && editingReferralFormData ? editingReferralFormData : request.form_data;
            
            return (
              <div className="space-y-4">
                {/* Header Info - Grid Layout */}
                <div className="bg-purple-50 p-4 rounded border border-purple-200">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div>
                      <p className="text-xs text-gray-600">Client</p>
                      <p className="text-sm font-medium">{requestClient ? formatClientName(requestClient) : request.client_name}</p>
                    </div>
                    {requestClient?.mrn && (
                      <div>
                        <p className="text-xs text-gray-600">MRN</p>
                        <p className="text-sm font-medium">{formatMrn(requestClient.mrn)}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-600">Requested By</p>
                      <p className="text-sm font-medium">{request.requested_by_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Date Submitted</p>
                      <p className="text-sm font-medium">{new Date(request.created_at).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Status</p>
                      <Badge className={
                        request.status === 'pending' ? 'bg-yellow-600' :
                        request.status === 'approved' ? 'bg-green-600' :
                        request.status === 'denied' ? 'bg-red-600' :
                        'bg-orange-600'
                      }>
                        {request.status.toUpperCase().replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                {/* Form Data - Grid Layout */}
                {currentFormData && template && template.form_fields && (
                  <div className="border rounded p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-900">Referral Information</h4>
                      {canEdit && (
                        <Badge className="bg-orange-100 text-orange-800">Edit Mode - Update your referral</Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(() => {
                        // Track which form_data keys we've displayed
                        const displayedKeys = new Set();
                        const renderedFields = [];
                        
                        // First, render fields that match template
                        template.form_fields.forEach((field) => {
                          const value = currentFormData[field.id];
                          // Skip section headers
                          if (field.type === 'section_header') return;
                          // In view mode, skip empty values
                          if (!canEdit && (!value || value === '')) return;
                          // Skip base64 encoded data (like signatures)
                          if (typeof value === 'string' && /^[A-Za-z0-9+/]{20,}={0,2}$/.test(value.trim())) return;
                          
                          displayedKeys.add(field.id);
                          
                          const isLongField = field.type === 'textarea' || 
                            (typeof value === 'string' && value.length > 100) ||
                            field.label.toLowerCase().includes('medications') ||
                            field.label.toLowerCase().includes('diagnoses') ||
                            field.label.toLowerCase().includes('allergies') ||
                            field.label.toLowerCase().includes('comments') ||
                            field.label.toLowerCase().includes('details') ||
                            field.label.toLowerCase().includes('summary');
                          
                          if (canEdit) {
                            // Editable field
                            renderedFields.push(
                              <div key={field.id} className={isLongField ? 'md:col-span-2' : ''}>
                                <div className="bg-orange-50 p-3 rounded border border-orange-200">
                                  <Label className="text-xs text-gray-700 mb-1">{field.label}</Label>
                                  {field.type === 'textarea' || isLongField ? (
                                    <DebouncedTextarea
                                      value={editingReferralFormData?.[field.id] || ''}
                                      onChange={(e) => setEditingReferralFormData(prev => ({
                                        ...prev,
                                        [field.id]: e.target.value
                                      }))}
                                      rows={4}
                                      className="mt-1"
                                      debounceMs={100}
                                    />
                                  ) : field.type === 'checkbox' ? (
                                    <div className="flex items-center gap-2 mt-1">
                                      <Checkbox
                                        checked={editingReferralFormData?.[field.id] || false}
                                        onCheckedChange={(checked) => setEditingReferralFormData(prev => ({
                                          ...prev,
                                          [field.id]: checked
                                        }))}
                                      />
                                      <span className="text-sm">{editingReferralFormData?.[field.id] ? 'Yes' : 'No'}</span>
                                    </div>
                                  ) : field.type === 'select' && field.options ? (
                                    <Select
                                      value={editingReferralFormData?.[field.id] || ''}
                                      onValueChange={(val) => setEditingReferralFormData(prev => ({
                                        ...prev,
                                        [field.id]: val
                                      }))}
                                    >
                                      <SelectTrigger className="mt-1">
                                        <SelectValue placeholder="Select..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {field.options.map((opt, idx) => {
                                          const optValue = typeof opt === 'object' ? (opt.value || opt.label || '') : opt;
                                          const optLabel = getOptionLabel(opt);
                                          return <SelectItem key={optValue || idx} value={optValue || `opt-${idx}`}>{optLabel}</SelectItem>;
                                        })}
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <DebouncedInput
                                      value={editingReferralFormData?.[field.id] || ''}
                                      onChange={(e) => setEditingReferralFormData(prev => ({
                                        ...prev,
                                        [field.id]: e.target.value
                                      }))}
                                      className="mt-1"
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          } else {
                            // Read-only field
                            renderedFields.push(
                              <div key={field.id} className={isLongField ? 'md:col-span-2' : ''}>
                                <div className="bg-gray-50 p-3 rounded">
                                  <p className="text-xs text-gray-600 mb-1">{field.label}</p>
                                  <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                    {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                                  </p>
                                </div>
                              </div>
                            );
                          }
                        });
                        
                        // Then, render any form_data keys not in template (legacy/custom fields)
                        Object.entries(currentFormData).forEach(([key, value]) => {
                          if (displayedKeys.has(key)) return;
                          if (!canEdit && (!value || value === '')) return;
                          if (typeof value === 'string' && /^[A-Za-z0-9+/]{20,}={0,2}$/.test(value.trim())) return;
                          
                          const isLongField = (typeof value === 'string' && value.length > 100);
                          const label = key.startsWith('field_') 
                            ? key.replace('field_', 'Custom Field ') 
                            : key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                          
                          if (canEdit) {
                            renderedFields.push(
                              <div key={key} className={isLongField ? 'md:col-span-2' : ''}>
                                <div className="bg-orange-50 p-3 rounded border border-orange-200">
                                  <Label className="text-xs text-gray-700 mb-1">{label}</Label>
                                  {isLongField ? (
                                    <DebouncedTextarea
                                      value={editingReferralFormData?.[key] || ''}
                                      onChange={(e) => setEditingReferralFormData(prev => ({
                                        ...prev,
                                        [key]: e.target.value
                                      }))}
                                      rows={4}
                                      className="mt-1"
                                    />
                                  ) : (
                                    <DebouncedInput
                                      value={editingReferralFormData?.[key] || ''}
                                      onChange={(e) => setEditingReferralFormData(prev => ({
                                        ...prev,
                                        [key]: e.target.value
                                      }))}
                                      className="mt-1"
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          } else {
                            renderedFields.push(
                              <div key={key} className={isLongField ? 'md:col-span-2' : ''}>
                                <div className="bg-blue-50 p-3 rounded">
                                  <p className="text-xs text-blue-600 mb-1">{label}</p>
                                  <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                    {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : 
                                     typeof value === 'object' ? JSON.stringify(value, null, 2) : 
                                     value}
                                  </p>
                                </div>
                              </div>
                            );
                          }
                        });
                        
                        return renderedFields.length > 0 ? renderedFields : (
                          <p className="text-gray-500 col-span-2">No referral data available</p>
                        );
                      })()}
                    </div>
                  </div>
                )}
                
                {/* Show raw form data if template not found */}
                {request.form_data && !template && (
                  <div className="border rounded p-4">
                    <h4 className="font-semibold mb-4 text-gray-900">Referral Information</h4>
                    <p className="text-sm text-orange-600 mb-3">⚠️ Form template not found. Showing raw data:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(request.form_data).map(([key, value]) => {
                        if (!value || value === '') return null;
                        if (typeof value === 'string' && /^[A-Za-z0-9+/]{20,}={0,2}$/.test(value.trim())) return null;
                        
                        const isLongField = (typeof value === 'string' && value.length > 100);
                        
                        return (
                          <div key={key} className={isLongField ? 'md:col-span-2' : ''}>
                            <div className="bg-gray-50 p-3 rounded">
                              <p className="text-xs text-gray-600 mb-1">{key.replace(/_/g, ' ').toUpperCase()}</p>
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : 
                                 typeof value === 'object' ? JSON.stringify(value, null, 2) : 
                                 value}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Additional Info Sections */}
                {request.more_info_question && (
                  <div className="bg-orange-50 border border-orange-200 rounded p-4">
                    <p className="text-sm font-medium text-orange-900 mb-2">
                      <MessageSquare className="inline h-4 w-4 mr-1" />
                      Question from Medication Team
                    </p>
                    <p className="text-sm text-orange-800 mb-3 whitespace-pre-wrap">{request.more_info_question}</p>
                    
                    {request.more_info_answer ? (
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <p className="text-sm font-medium text-green-900 mb-2">Your Response</p>
                        <p className="text-sm text-green-800 whitespace-pre-wrap">{request.more_info_answer}</p>
                      </div>
                    ) : (
                      /* Show response form if current user is the requester and status is needs_more_info */
                      request.status === 'needs_more_info' && request.requested_by_id === user?.id && (
                        <div className="mt-3 pt-3 border-t border-orange-200">
                          <p className="text-sm font-medium text-orange-900 mb-2">Your Response</p>
                          <DebouncedTextarea
                            id="med-referral-response"
                            placeholder="Enter your response to the medication team's question..."
                            rows={4}
                            className="w-full mb-3"
                          />
                          <div className="flex gap-2">
                            <Button
                              className="bg-orange-600 hover:bg-orange-700"
                              onClick={async () => {
                                const answer = document.getElementById('med-referral-response').value;
                                if (!answer || answer.trim() === '') {
                                  toast.error("Please provide a response");
                                  return;
                                }
                                try {
                                  await axios.put(`${API}/medication-referral-requests/${request.id}/answer`, {
                                    more_info_answer: answer,
                                    form_data: editingReferralFormData || request.form_data
                                  });
                                  toast.success("Response submitted - referral resubmitted for review");
                                  setSelectedReferralForDetails(null);
                                  setEditingReferralFormData(null);
                                  fetchMedicationReferralRequests();
                                } catch (error) {
                                  console.error("Failed to submit answer:", error);
                                  const errorMsg = error.response?.data?.detail || error.message || "Failed to submit response";
                                  toast.error(errorMsg);
                                }
                              }}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Submit Response & Updated Info
                            </Button>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}
                
                {/* Previous info-request exchanges (archived when a new question is asked) */}
                {Array.isArray(request.more_info_history) && request.more_info_history.length > 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded p-4" data-testid="med-referral-info-history">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      <MessageSquare className="inline h-4 w-4 mr-1" />
                      Previous Info Requests
                    </p>
                    <div className="space-y-3">
                      {request.more_info_history.map((h, i) => (
                        <div key={i} className="border-l-2 border-gray-300 pl-3">
                          <p className="text-xs text-gray-500">
                            Asked{h.asked_by_name ? ` by ${h.asked_by_name}` : ''}{h.asked_at ? ` on ${new Date(h.asked_at).toLocaleDateString()}` : ''}
                          </p>
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{h.question}</p>
                          {h.answer ? (
                            <p className="text-sm text-gray-600 whitespace-pre-wrap mt-1">
                              <span className="font-medium">Reply{h.answered_by_name ? ` (${h.answered_by_name})` : ''}:</span> {h.answer}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-400 italic mt-1">No reply was recorded</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {request.status === 'denied' && request.review_notes && (
                  <div className="bg-red-50 border border-red-200 rounded p-4">
                    <p className="text-sm font-medium text-red-900 mb-2">Denial Reason</p>
                    <p className="text-sm text-red-800">{request.review_notes}</p>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
  );
};

export default MedicationReferralDetailsDialog;
